import React, { useState } from "react";
import * as motion from "motion/react-client";
import {
  LayoutDashboard,
  MessageCircle,
  Stethoscope,
  Users,
  ChevronDown,
  ChevronUp,
  User,
  ScanSearch,
} from "lucide-react";
import { useSelector } from "react-redux";
import { FaCog } from "react-icons/fa";
import { useNavigate, useLocation, NavLink } from "react-router-dom";
import MNRlogo from "../../assets/MNRLOGO.png";
import Logo from "../../assets/Doctor-AI_LOGO.png";

const navItems = [
  {
    id: "dashboard",
    label: "Dashboard",
    icon: LayoutDashboard,
    path: "/dashboard",
    role: "All", // visible to everyone
  },
  {
    id: "chatbot",
    label: "AI Chatbot",
    icon: MessageCircle,
    path: "/dashboard/chatbot",
    role: "Patient",
  },
  {
    id: "document-analysis",
    label: "AI Analysis",
    icon: ScanSearch,
    path: "/dashboard/document-analysis",
    role: "All", // patients and doctors both upload documents/X-rays
  },
  {
    id: "doctor",
    label: "Doctor",
    icon: Stethoscope,
    path: "/dashboard/doctor-management",
    role: "Doctor",
  },
  {
    id: "patient",
    label: "Patient",
    icon: Users,
    path: "/dashboard/patient-management",
    role: "Patient",
  },
];

const Sidebar = ({ isCollapsed }) => {
  const { user } = useSelector((state) => state.profile);
  const navigate = useNavigate();
  const location = useLocation();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  // Settings submenu links
  const settingsLinks = [
    {
      to: "/dashboard/settings/profile-management",
      label: "Profile Management",
    },
    {
      to: "/dashboard/settings/password-management",
      label: "Password Management",
    },
    ...(user?.role === "Admin"
      ? [
          {
            to: "/dashboard/settings/user-management",
            label: "User Management",
          },
        ]
      : []),
  ];

  // ✅ Filter visible items based on user role
  const visibleItems = navItems.filter(
    (item) =>
      item.role === "All" ||
      user?.role === "Admin" || // Admin can see all
      item.role === user?.role
  );

  return (
    <aside
      className={`p-4 flex flex-col transition-all duration-300 border-r ${
        isCollapsed ? "hidden md:block md:w-20" : "w-64"
      } bg-white dark:bg-[#00254D] text-black dark:text-white`}
    >
      {/* Logo */}
      <div className="flex items-center gap-3 p-2 mb-6 justify-center">
        {isCollapsed ? (
          <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center text-white">
            <img src={MNRlogo} alt="MNR logo" />
          </div>
        ) : (
          <div className="flex-shrink-0 w-40 h-10 flex items-center justify-center text-white">
            <img src={Logo} alt="Doctor AI logo" />
          </div>
        )}
      </div>


      {/* Nav Items */}
      <nav className="flex-1 space-y-2">
        {visibleItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <button
              key={item.id}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors text-left ${
                isCollapsed ? "justify-center" : ""
              } ${
                isActive
                  ? "bg-indigo-500 text-white dark:bg-[#003366]"
                  : "hover:bg-gray-100 dark:hover:bg-[#003366]"
              }`}
            >
              <item.icon />
              {!isCollapsed && <span>{item.label}</span>}
            </button>
          );
        })}

        {/* Settings Toggle */}
        {!isCollapsed && (
          <div className="mt-4">
            <button
              onClick={() => setIsSettingsOpen((prev) => !prev)}
              className="w-full flex items-center gap-3 p-3 rounded-lg transition-colors hover:bg-gray-100 dark:hover:bg-[#003366]"
            >
              <FaCog className="w-5 h-5" />
              <span>Settings</span>
              {isSettingsOpen ? (
                <ChevronUp className="ml-auto" />
              ) : (
                <ChevronDown className="ml-auto" />
              )}
            </button>

            {isSettingsOpen && (
              <div className="ml-6 mt-2 flex flex-col gap-2">
                {settingsLinks.map(({ to, label }) => (
                  <NavLink
                    key={label}
                    to={to}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 rounded-lg transition ${
                        isActive
                          ? "bg-indigo-100 text-indigo-600 dark:bg-[#003366] dark:text-white font-semibold"
                          : "text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-[#003366]"
                      }`
                    }
                  >
                    <span>{label}</span>
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        )}
      </nav>

      {/* Talk to AI Button */}
      <div className="mt-6 mb-6 flex flex-col items-center justify-center">
        <motion.button
          onClick={() => navigate("/voicebot")}
          whileHover={{ scale: 1.1 }}
          className="relative flex items-center justify-center p-4 rounded-full shadow-xl text-white overflow-hidden transition-all bg-gradient-to-r from-[#2aabdc] to-[#0c1c5c] focus:ring-blue-500"
        >
          <User className="w-8 h-8 z-10" />
          {[...Array(3)].map((_, i) => (
            <motion.span
              key={i}
              className="absolute rounded-full border-2 border-white/40"
              style={{ width: 40 + i * 15, height: 40 + i * 15 }}
              animate={{ scale: [1, 1.4, 1], opacity: [0.6, 0.2, 0] }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                delay: i * 0.4,
              }}
            />
          ))}
        </motion.button>

        <span className="mt-2 text-sm font-semibold text-[#00254D] dark:text-[#2aabdc] drop-shadow-[0_0_6px_#2aabdc]">
          Talk to our AI doctor
        </span>
      </div>
    </aside>
  );
};

export default Sidebar;
